import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  CheckBoxEmpty,
  CheckBoxFull,
  HidePwIcon,
  ShowPwIcon,
} from "../../assets/icons/icons";
import { Box, Select, MenuItem, FormControl } from "@mui/material";
import { NongCallLogo } from "../../assets/images/images";
import theme from "../../theme";
import bgImage1 from "../../assets/images/bgImage1.jpg";
import useLogin from "../../hooks/useLogin";
import UserRegisterModal from "../../components/userRegisterModal/userRegisterModal";
import { checkPressKeys } from "../../types/types";
import Footer from "../../components/footer";
import packageJson from "../../../package.json";
import { useSystemSetting } from "../../hooks/useSystemSetting";
import { SrLable } from "../../components/common/SrLabel";
import { useI18n, useLocaleWithStorage, Locale } from "../../locales";
import { LanguageFlag } from "../../assets/images/languageFlags";

const { sm, md } = theme.screen;

const Login: React.FC<{}> = () => {
  const { t } = useI18n();
  const { locale, setLocale } = useLocaleWithStorage();
  const [version, setVersion] = useState("");
  const [loginInfo, setLoginInfo] = useState({
    id: "",
    pw: "",
    option: false,
  });
  const { mutate: login, isError, error } = useLogin(loginInfo.option);
  const systemSetting = useSystemSetting();

  const [isFail, setIsFail] = useState(false);
  const [showErrMsg, setShowErrMsg] = useState(false);
  const [failedInfo, setFailedInfo] = useState({ id: "", pw: "" });
  const [showPw, setShowPw] = useState<"text" | "password">("password");
  const [openRegister, setOpenRegister] = useState(false);
  const [bgColor, setBgColor] = useState({
    id: "gray",
    pw: "gray",
  });

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginInfo((prev) => {
      return { ...prev, [e.target.name]: e.target.value };
    });
  };

  const onCheckChange = () => {
    setLoginInfo((prev) => {
      return { ...prev, option: !prev.option };
    });
  };

  const toggleIcon = () => {
    setShowPw((prev) => {
      if (prev === "text") {
        return "password";
      } else {
        return "text";
      }
    });
  };

  const canSubmit = loginInfo.id.length > 0 && loginInfo.pw.length > 0;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    login({ id: loginInfo.id, pw: loginInfo.pw });
  };

  useEffect(() => {
    // 4009 : 아이디/비밀번호 불일치 (그 외 에러는 훅에서 처리)
    if (error?.response?.data.code === 4009) {
      if (isError) {
        setIsFail(isError);
        setShowErrMsg(true);
        setFailedInfo({ id: loginInfo.id, pw: loginInfo.pw });
      }
    }
  }, [isError, error]);

  useEffect(() => {
    if (isFail) {
      if (
        failedInfo.id !== loginInfo.id ||
        failedInfo.pw !== loginInfo.pw
      ) {
        setShowErrMsg(false);
      }
    }
  }, [isFail, loginInfo.id, loginInfo.pw, failedInfo]);

  useEffect(() => {
    if (showErrMsg) {
      setBgColor({ id: "red", pw: "red" });
      return;
    } else {
      if (!loginInfo.id) {
        setBgColor((prev) => ({ ...prev, id: "gray" }));
      }
      if (loginInfo.id) {
        setBgColor((prev) => ({ ...prev, id: "blue" }));
      }
      if (!loginInfo.pw) {
        setBgColor((prev) => ({ ...prev, pw: "gray" }));
      }
      if (loginInfo.pw) {
        setBgColor((prev) => ({ ...prev, pw: "blue" }));
      }
    }
  }, [loginInfo, showErrMsg]);

  // 키보드 이벤트
  const [pressedKeys, setPressedKeys] = useState<checkPressKeys[]>([]);
  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // ESC 누르면 배열 비움

    if (e.code === "Escape") {
      setPressedKeys([]);
    }
    if (e.code && pressedKeys) {
      if (pressedKeys.length < 5) {
        setPressedKeys([
          ...pressedKeys,
          { id: e.code, contents: e.code.toString() },
        ]);
      }
    }
    // console.log(e.code);
    // console.log(pressedKeys);
    return;
  };

  if (pressedKeys.length === 4) {
    let code = ["KeyN", "KeyE", "KeyW", "ShiftRight"];
    let check = 0;
    pressedKeys.map((key) => {
      if (code.includes(key.contents)) {
        let index = code.indexOf(key.contents);
        code.splice(index, 1);
        check++;
        // console.log(pressedKeys);
        // console.log(check);
      }
    });
    setPressedKeys([]);
    if (check === 4) {
      setOpenRegister(true);
    }
  }

  // 버전 가져오기
  useEffect(() => {
    // 개발계 - 릴리즈 포함
    if (window.location.href.includes("dev")) {
      setVersion(packageJson.version);
    } else {
      // 운영계 - 릴리즈 미포함
      setVersion(
        packageJson.version.slice(0, packageJson.version.indexOf("-"))
      );
    }
  }, []);

  // console.log(window.location.href);
  // console.log(window.location.href.includes("dev"));

  return (
    <LoginContainer
      tabIndex={0}
      onKeyDown={handleKeyPress}
      onKeyUp={() => setPressedKeys([])}
    >
      <Wrapper bgImage={bgImage1}>
        <LoginWrapper>
          <LogoWrapper>
            <NongCallLogo />
          </LogoWrapper>
          <h1>{t.login.title}</h1>

          <LoginForm onSubmit={onSubmit}>
            <SrLable id="id" labelText={t.login.id} />
            <TextInput
              id="id"
              autoComplete="off"
              type="text"
              placeholder={t.login.idPlaceholder}
              name="id"
              onChange={onTextChange}
              value={loginInfo.id}
              onFocus={() => {
                if (showErrMsg) return;
                setBgColor({ ...bgColor, id: "blue" });
              }}
              onBlur={() => {
                if (showErrMsg) return;
                loginInfo.id
                  ? setBgColor({ ...bgColor, id: "blue" })
                  : setBgColor({ ...bgColor, id: "gray" });
              }}
              bgColor={bgColor.id}
            />

            <SrLable id="pw" labelText={t.login.password} />
            <TextInput
              id="pw"
              autoComplete="off"
              type={showPw}
              placeholder={t.login.passwordPlaceholder}
              name="pw"
              onChange={onTextChange}
              value={loginInfo.pw}
              onFocus={() => {
                if (showErrMsg) return;
                setBgColor({ ...bgColor, pw: "blue" });
              }}
              onBlur={() => {
                if (showErrMsg) return;
                loginInfo.pw
                  ? setBgColor({ ...bgColor, pw: "blue" })
                  : setBgColor({ ...bgColor, pw: "gray" });
              }}
              bgColor={bgColor.pw}
            />

            <CheckBoxWrapper>
              <p style={{ paddingLeft: "5px" }} onClick={onCheckChange}>
                {t.login.keepLoggedIn}
              </p>
              <CheckBoxIconWrapper onClick={onCheckChange} type="button">
                {loginInfo.option ? <CheckBoxFull /> : <CheckBoxEmpty />}
              </CheckBoxIconWrapper>
            </CheckBoxWrapper>
            <LoginButton canSubmit={canSubmit} disabled={!canSubmit}>
              {t.login.loginButton}
            </LoginButton>

            <IconWrapper onClick={toggleIcon} type="button">
              {showPw === "password" ? <ShowPwIcon /> : <HidePwIcon />}
            </IconWrapper>
            {showErrMsg && (
              <ErrorMsg dangerouslySetInnerHTML={{ __html: t.login.invalidCredentials }} />
            )}
          </LoginForm>

          {/* 언어 선택 및 버전 정보 */}
          <LanguageAndVersionWrapper>
            { false && <LanguageSelectFormControl>
              <Select
                value={locale}
                onChange={(e) => setLocale(e.target.value as Locale)}
                sx={{
                  "& .MuiSelect-select": {
                    padding: "8px 32px 8px 12px",
                    fontSize: "14px",
                  },
                }}
              >
                <MenuItem value="ko">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <LanguageFlag locale="ko" />
                    <span>한국어</span>
                  </Box>
                </MenuItem>
                <MenuItem value="en">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <LanguageFlag locale="en" />
                    <span>English</span>
                  </Box>
                </MenuItem>
                <MenuItem value="zh">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <LanguageFlag locale="zh" />
                    <span>中文</span>
                  </Box>
                </MenuItem>
                <MenuItem value="ja">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <LanguageFlag locale="ja" />
                    <span>日本語</span>
                  </Box>
                </MenuItem>
                <MenuItem value="vi">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <LanguageFlag locale="vi" />
                    <span>Tiếng Việt</span>
                  </Box>
                </MenuItem>
                <MenuItem value="es">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <LanguageFlag locale="es" />
                    <span>Español</span>
                  </Box>
                </MenuItem>
              </Select>
            </LanguageSelectFormControl>}

            <VersionText>
              {`V ${version}`}
            </VersionText>
          </LanguageAndVersionWrapper>
        </LoginWrapper>

        {/* 회원가입 모달 */}
        <UserRegisterModal
          grade={2}
          open={openRegister}
          onPressNo={() => setOpenRegister(false)}
          systemSetting={systemSetting("admin")}
        />
      </Wrapper>
      <Footer />
    </LoginContainer>
  );
};
const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${sm}px) {
    min-height: -webkit-fill-available;
    position: fixed;
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
    }
  }

  @media (min-width: ${sm + 1}px) {
    height: 100vh;
  }
`;
const Wrapper = styled.div<{ bgImage: string }>`
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  background: url(${(props) => props.bgImage});
  background-repeat: no-repeat;
  background-size: cover;
`;

const LoginWrapper = styled.div`
  border-radius: 20px;
  background-color: ${theme.color.white};
  position: relative;

  h1 {
    font-size: 21px;
    font-weight: 700;
    line-height: 25px;
    color: ${theme.color.black};
    margin-bottom: 31px;

    @media (max-width: ${sm}px) {
      margin-bottom: 15px;
    }
  }
  @media (max-width: ${sm}px) {
    min-width: 340px;
    width: 90vw;
    height: 450px;
    padding: 20px;
    padding-bottom: 20px;
  }
  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    width: 90vw;
    max-width: 447px;
    padding-top: 55px;
    padding-bottom: 55px;
    padding-left: 50px;
    padding-right: 50px;
  }
  @media (min-width: ${md + 1}px) {
    min-width: 477px;
    padding-top: 55px;
    padding-bottom: 55px;
    padding-left: 50px;
    padding-right: 50px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 45px;
  @media (max-width: ${sm}px) {
    margin-bottom: 10px;
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  position: relative;
  @media (max-width: ${sm}px) {
    gap: 10px;
  }
`;

const TextInput = styled.input<{ bgColor?: string }>`
  font-size: 15px;
  height: 54px;
  border-radius: 10px;
  border: none;
  padding-left: 20px;
  color: ${theme.color.black};
  background-color: ${({ bgColor }) =>
    bgColor == "red"
      ? `${theme.statusColor.INPUT_ERROR}`
      : bgColor == "blue"
      ? `${theme.statusColor.INPUT_ACTIVE}`
      : `${theme.statusColor.INPUT_DEFAULT}`};
  &::placeholder {
    font-size: 15px;
  }
  &:first-of-type {
    margin-bottom: 20px;

    @media (max-width: ${sm}px) {
      margin-bottom: 10px;
    }
  }

  // 크롬 자동완성시 인풋 배경색 설정
  &:-webkit-autofill {
    -webkit-box-shadow: ${({ bgColor }) =>
      bgColor == "red"
        ? `0 0 0 30px ${theme.statusColor.INPUT_ERROR} inset`
        : `0 0 0 30px ${theme.statusColor.INPUT_ACTIVE} inset`};
  }
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 42px;
  margin-bottom: 75px;
  position: relative;
  p {
    font-size: 13px;
    font-weight: 400;
    line-height: 15px;
    color: ${theme.color.neutral_700};
    margin-left: 20px;
    cursor: pointer;
    margin-left: 24px;
  }
  @media (max-width: ${sm}px) {
    margin-top: 20px;
    margin-bottom: 50px;
  }
`;

const CheckBoxIconWrapper = styled.button`
  position: absolute;
  transform: translateY(1px);
  cursor: pointer;
`;

const LoginButton = styled.button<{ canSubmit: boolean }>`
  background: ${({ canSubmit }) =>
    canSubmit ? theme.statusColor.MAIN : theme.statusColor.DISABLED};
  border-radius: 10px;
  cursor: ${({ canSubmit }) => (canSubmit ? "pointer" : "default")};
  border: none;
  color: ${theme.color.white};
  padding: 15px 0px;

  ${({ canSubmit }) =>
    canSubmit
      ? `&:hover {background-color: ${theme.statusColor.MAIN_HOVER};}`
      : theme.statusColor.DISABLED}

  @media (max-width: ${sm}px) {
    padding-bottom: 10px;
  }
  @media (min-width: ${sm + 1}px) {
    margin-bottom: 10px;
  }
`;

const LanguageSelectFormControl = styled(FormControl)`
  flex: 1;
  max-width: 200px;
`;

const IconWrapper = styled.button`
  position: absolute;
  top: 91px;
  right: 20px;
  cursor: pointer;
  height: 20px;
  display: flex;
  align-items: center;
`;

const ErrorMsg = styled.div`
  left: 5px;
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.red_500};
  position: absolute;
  bottom: 155px;
  @media (max-width: ${sm}px) {
    bottom: 180px;
  }
  @media (min-width: ${sm + 1}px) {
    bottom: 240px;
  }
`;

const VersionText = styled.div`
  text-align: right;
  padding: 5px 0;
  font-size: 12px;
  color: ${theme.color.neutral_600};
`;

const LanguageAndVersionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  position: relative;
  width: 100%;

  @media (max-width: ${sm}px) {
    margin-top: 5px;
  }
`;

export default Login;
